import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface FeatureVisibility {
  showMedications: boolean;
  showAllergies: boolean;
  showChildren: boolean;
  showDiary: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const FeatureVisibilityContext = createContext<FeatureVisibility>({
  showMedications: true,
  showAllergies: true,
  showChildren: true,
  showDiary: true,
  loading: true,
  refresh: async () => {},
});

export function FeatureVisibilityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [showMedications, setShowMedications] = useState(true);
  const [showAllergies, setShowAllergies] = useState(true);
  const [showChildren, setShowChildren] = useState(true);
  const [showDiary, setShowDiary] = useState(true);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setShowMedications(true);
      setShowAllergies(true);
      setShowChildren(true);
      setShowDiary(true);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("profiles")
      .select("show_medications, show_allergies, show_children, show_diary")
      .eq("user_id", user.id)
      .maybeSingle();
    setShowMedications((data as any)?.show_medications ?? true);
    setShowAllergies((data as any)?.show_allergies ?? true);
    setShowChildren((data as any)?.show_children ?? true);
    setShowDiary((data as any)?.show_diary ?? true);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({ showMedications, showAllergies, showChildren, showDiary, loading, refresh: load }),
    [showMedications, showAllergies, showChildren, showDiary, loading, load],
  );

  return (
    <FeatureVisibilityContext.Provider value={value}>{children}</FeatureVisibilityContext.Provider>
  );
}

export const useFeatureVisibility = () => useContext(FeatureVisibilityContext);
